import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Spaetestens nach dieser Zeit wird der Drawer aufgeraeumt, auch wenn die
 * Schliess-Animation kein Ende meldet. Bewusst grosszuegig gegenueber
 * --drawer-animation-speed (0.2s), damit die normale Animation ungestoert laeuft.
 */
const CLOSE_CLEANUP_FALLBACK_MS = 500;

/**
 * Notbremse fuer den Oeffnen-Weg: Spaetestens hier bekommt das <details> seine
 * menu-open-Klasse, damit der Drawer nie unsichtbar offen steht.
 */
const OPEN_SHOW_FALLBACK_MS = 120;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** Laufendes Zeitfenster des Oeffnen-Wegs — wird beim Schliessen abgeraeumt. */
  #openShowTimer = 0;

  /** R560: Scrollstand beim Oeffnen (Fenster UND .page-wrapper), wird nach dem Schliessen wiederhergestellt. */
  #scrollBeimOeffnen = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // R560: Escape wirkte nur, wenn der Fokus IN der Schublade lag — am Handy liegt er fast nie dort
    // (gemessen: activeElement = BODY bei offener Schublade, Escape ohne Wirkung). Jetzt am Dokument.
    document.addEventListener('keydown', this.#onDocKeyDown);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('keydown', this.#onDocKeyDown);
  }

  /** @param {KeyboardEvent} event */
  #onDocKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    if (this.contains(/** @type {Node} */ (event.target))) return; // das erledigt #onKeyUp
    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      const wrapper = document.querySelector('.page-wrapper');
      this.#scrollBeimOeffnen = { fenster: window.scrollY, wrapper: wrapper ? wrapper.scrollTop : 0 };
    }

    this.preventInitialAccordionAnimations(details);

    // US-Parts24: menu-open MUSS kommen. Sichtbar ist der Drawer nur ueber diese
    // Klasse — eingefroren ist die Seite dagegen schon durch das [open] am <details>
    // (html[scroll-lock] = overflow:hidden). Laeuft requestAnimationFrame nicht
    // (Seite rendert gerade nicht), stuende sonst ein unsichtbares Menue vor einer
    // Seite, die sich nicht mehr scrollen laesst. Also: rAF ODER Zeitfenster.
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    };

    requestAnimationFrame(show);
    clearTimeout(this.#openShowTimer);
    this.#openShowTimer = setTimeout(show, OPEN_SHOW_FALLBACK_MS);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    clearTimeout(this.#openShowTimer);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // US-Parts24: Das Aufraeumen darf NICHT allein an der Schliess-Animation haengen.
    // Beendet der Browser die Transition nicht (Tab im Hintergrund, Rendern pausiert,
    // zweite Overlay-Ebene, iOS-Energiesparmodus), bleibt sonst [open] am <details>
    // stehen — und damit html[scroll-lock] (overflow:hidden) plus der unsichtbare
    // .menu-drawer__backdrop ueber dem ganzen Viewport: Die Seite laesst sich nicht
    // mehr scrollen, bis der Menue-Knopf ein zweites Mal gedrueckt wird.
    // Deshalb: Animationsende ODER Zeitfenster — was zuerst kommt, genau einmal.
    let cleanedUp = false;
    const cleanUp = () => {
      if (cleanedUp) return;
      cleanedUp = true;

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        this.#scrollZurueck();
      } else {
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(drawer || details, cleanUp, { subtree: false });
    setTimeout(cleanUp, CLOSE_CLEANUP_FALLBACK_MS);
  }

  /**
   * R560: Nach dem Schliessen sprang die Seite am Handy nach oben (R542-Befund: scrollY 600 → 0,
   * in Live- und Vorschau-Theme). Im Pruefbrowser nicht nachstellbar — wahrscheinlicher Weg ist ein
   * focus() ohne preventScroll auf den Menue-Knopf im Kopf (er liegt beim Scrollen ueber dem Bild,
   * gemessen top = -820). Statt jeden moeglichen Fokus-Weg einzeln zu jagen: Scrollstand beim
   * Oeffnen merken und nach dem Schliessen wiederherstellen, wenn er sich um mehr als 40 px bewegt hat.
   */
  #scrollZurueck() {
    const vorher = this.#scrollBeimOeffnen;
    this.#scrollBeimOeffnen = null;
    if (!vorher) return;
    const wrapper = document.querySelector('.page-wrapper');
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - vorher.fenster) > 40) window.scrollTo({ top: vorher.fenster, behavior: 'instant' });
      if (wrapper && Math.abs(wrapper.scrollTop - vorher.wrapper) > 40) wrapper.scrollTop = vorher.wrapper;
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
